import {
  useState,
  useRef,
  useEffect,
  useCallback,
  MutableRefObject,
  useMemo,
  useImperativeHandle,
  Ref,
  forwardRef
} from 'react';
import debounce from 'lodash/debounce';
import useGetDrugListByName from 'src/api/hooks/useGetDrugListByName';
import NextImage from 'src/shared-components/NextImage/NextImage';
import { useRouter } from 'next/router';
import { generateSearchPageLink } from 'src/utils/links';
import { FastSearchCategory } from 'src/api/types';
import { GetFastDrugSearhResponse } from 'src/api/requests/getFastDrugSearch';
import Input from 'src/shared-components/Input/Input/Input';
import {
  ModalPosition,
  ModalRounding
} from 'src/shared-components/Modal/Modal';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import useModalStore from 'src/hooks/useModalStore';
import dynamic from 'next/dynamic';
import useViewedDrugsStore from 'src/hooks/useViewedDrugsStore';
import { ItemListName } from 'src/api/analytic.types';
import { getAnalyticPreparedDrugList } from 'src/hooks/useAnalytics/useAnalytics.helpers';
import useNotification, { NotificationType } from 'src/hooks/useNotification';
import useSearchHistoryStore from 'src/hooks/useSearchHistoryStore';
import useGTAG, { GTAGPrefix } from 'src/hooks/useGTAG';
import useMedia from 'src/hooks/useMedia';
import useTopDrugsStore from 'src/hooks/useTopDrugsStore';
import trns from './trns';

const SearchResults = dynamic(() => import('./SearchResultsV2'), {
  ssr: false
});

const Modal = dynamic(() => import('src/shared-components/Modal/Modal'), {
  ssr: false
});

const clearIcon = 'shared/clear-icon.svg';
const searchIcon = 'shared/header-search.svg';
const searchIconOrange = 'shared/search-orange-icon.svg';
const searchIconDark = 'shared/header-search-dark.svg';

export interface RefType {
  handleCleanInput: () => void;
}

interface Props {
  parentRef?: MutableRefObject<HTMLElement>;
  isBookingCart?: boolean;
  inputId?: string;
  gtagPrefix: GTAGPrefix;
  onHandleInputActions?: () => void;
  isAnotherModalOpened?: boolean;
  style?: { [key: string]: string };
  withShadowBackground?: boolean;
  modalContainerClassName?: string;
  fixedModalPosition?: boolean;
  isOnHeader?: boolean;
  dataCy?: string;
  containerClassName?: string;
  globalContainerClassName?: string;
}

const SearchInputV2 = forwardRef((props: Props, ref: Ref<RefType> = null) => {
  const {
    parentRef = null,
    isBookingCart = false,
    inputId,
    gtagPrefix,
    onHandleInputActions,
    isAnotherModalOpened = false,
    style,
    withShadowBackground = true,
    modalContainerClassName,
    fixedModalPosition,
    isOnHeader = false,
    dataCy,
    containerClassName,
    globalContainerClassName
  } = props;
  const intl = useIntl();
  const { showNotification } = useNotification();
  const { trackGtagEvent } = useGTAG();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchValue, setSearchValue] = useState<string>('');
  const [initialCategoriesList, setInitialCategoriesList] =
    useState<FastSearchCategory[]>(null);
  const [searchResult, setSearchResult] =
    useState<GetFastDrugSearhResponse | null>(null);
  const [searchResultCount, setSearchResultCount] = useState<number>(0);
  const { isModalVisible, updateModalsConfig } = useModalStore();
  const [showRecommendations, setRecommendations] = useState<boolean>(false);
  const [isSearchTried, setSearchTried] = useState<boolean>(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  // Send GA event only once
  const inputRef = useRef<HTMLInputElement>(null);
  const { formatMessage } = useIntl();
  const router = useRouter();

  const { getDrugListByName, isLoading: searchInProgress } =
    useGetDrugListByName();
  const { addSearchValuesToHistory } = useSearchHistoryStore();
  const { viewedDrugsList } = useViewedDrugsStore();
  const { topDrugsList } = useTopDrugsStore();

  const modalVisible = isModalVisible('search-input-v2-modal');
  const { pathname } = useRouter();
  const isHomePage = pathname === '/';
  const { sm } = useMedia();

  const modalPosition = () => {
    if (fixedModalPosition && isHomePage)
      return ModalPosition.RelativeScrollBottomPageCenter;
    if (isBookingCart) return ModalPosition.RelativeBottom;
    return ModalPosition.RelativeCenter;
  };

  useEffect(() => {
    if (isAnotherModalOpened) {
      updateModalsConfig('search-input-v2-modal', false);
    }
  }, [isAnotherModalOpened]);

  useEffect(() => {
    setRecommendations(searchValue?.length === 0 && modalVisible);
  }, [searchValue, modalVisible]);

  const onSearchEnter = async (value) => {
    setIsLoading(true);
    const result = await getDrugListByName({ text: value });
    setSearchResult(result);
    setSearchResultCount((state) => state + 1);
    setInitialCategoriesList(result?.categories);
    setSearchTried(true);
    updateModalsConfig('search-input-v2-modal', true);
    setIsLoading(false);

    if (value?.length >= 3) {
      if (result.drugs?.length === 0) {
        trackGtagEvent(`${gtagPrefix}_empty`, value, 'search_request');
      } else {
        trackGtagEvent(`${gtagPrefix}_done`, value, 'search_request');
      }
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnSearchEnter = useCallback(
    debounce((value: string) => onSearchEnter(value), 250),
    []
  );

  // Debounce search value update to reduce re-renders
  const debouncedOnSearchValueUpdate = useCallback(
    debounce((value: string) => setSearchValue(value), 250),
    []
  );

  const handleInputActions = () => {
    if (onHandleInputActions) {
      onHandleInputActions();
    }
  };

  const handleCleanInput = () => {
    if (inputRef?.current) {
      inputRef.current.value = '';
    }
    debouncedOnSearchValueUpdate('');
  };

  useImperativeHandle(ref, () => ({ handleCleanInput }));

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // input's onBlur causes onChange trigger with "change" nativeEvent type. This causes request duplication
    // by default nativeEvent't type is "input"
    if (event.nativeEvent.type === 'input') {
      const { value: rawValue } = event.currentTarget;
      const value = rawValue.trim();
      debouncedOnSearchValueUpdate(rawValue);
      // clean up category on new value enter
      setSelectedCategoryId(null);
      // Reset search result if input was cleaned up to show loading on initial type
      if (value === '') {
        setSearchResult(null);
        setSearchResultCount((state) => state + 1);
      } else {
        debouncedOnSearchEnter(value);
      }
    }
    handleInputActions();
  };

  const handleFocus = () => {
    setRecommendations(searchValue?.length === 0);
    updateModalsConfig('search-input-v2-modal', true);
    handleInputActions();
  };

  const handleRedirectToSearchWithValue = async (value: string) => {
    await router.push(
      generateSearchPageLink({
        search: value
      })
    );
    updateModalsConfig('search-input-v2-modal', false);
    handleCleanInput();
  };

  const handleSubmit = async (ev = null) => {
    const formValue = ev?.target[1]?.value.trim();

    if (searchValue?.length < 3) {
      // update search state with form value
      debouncedOnSearchValueUpdate(formValue);
    }

    updateModalsConfig('search-input-v2-modal', false);
    inputRef?.current?.blur();
    if (ev) {
      ev.preventDefault();
    }

    const valueForSearch = searchValue?.length >= 3 ? searchValue : formValue;
    if (valueForSearch?.length >= 3) {
      addSearchValuesToHistory(valueForSearch);
      await handleRedirectToSearchWithValue(valueForSearch);
    } else {
      showNotification({
        message: intl.formatMessage(trns.warning),
        type: NotificationType.Warning
      });
    }
  };

  const hideSearchResults = () => {
    updateModalsConfig('search-input-v2-modal', false);
  };

  const onRefetchWithCategory = async (text: string, categoryId: string) => {
    setIsLoading(true);
    const result = await getDrugListByName({ text, categoryId });
    setSearchResult(result);
    setSearchResultCount((state) => state + 1);
    setIsLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnCategoryChangeEnter = useCallback(
    debounce((value: string) => onRefetchWithCategory(searchValue, value), 250),
    [searchValue]
  );

  const onCategoryHover = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    debouncedOnCategoryChangeEnter(categoryId);
  };

  const viewedRecommendationData =
    viewedDrugsList?.length > 0 ? viewedDrugsList : topDrugsList;

  const drugListData = searchResult?.drugs || null;
  const tradeNamesData = searchResult?.tradeNames || null;

  const drugsData = showRecommendations
    ? viewedRecommendationData
    : drugListData;

  const drugListWithAnalyticData = useMemo(
    () =>
      getAnalyticPreparedDrugList({
        drugs: drugsData,
        itemListName: ItemListName.SpeedSearch
      }),
    [searchValue, searchResultCount, handleFocus]
  );

  const searchIconBlue = searchValue ? searchIconDark : searchIcon;
  const borderStyle = sm && isHomePage && '!border-primary';

  return (
    <div
      className={cn(
        'static w-full h-full flex mx-2',
        isHomePage ? 'sm:mx-0' : 'sm:mx-9',
        containerClassName
      )}
    >
      <form
        className="z-60 flex items-center h-full w-full "
        onSubmit={handleSubmit}
      >
        <Input
          ref={inputRef}
          id={inputId}
          data-cy={dataCy}
          name="searchInput"
          placeholder={formatMessage(trns.enterTitle)}
          slim="true"
          inputWrapperClassName={`w-full ${borderStyle}`}
          onChange={handleSearchChange}
          onFocus={() => {
            handleFocus();
            trackGtagEvent(`${gtagPrefix}_click`);
          }}
          onClick={handleInputActions}
          autoCompleteNeeded={false}
          leftPlaceComponent={
            <button className="h-full flex items-center pl-3.5" type="submit">
              <div>
                <NextImage
                  alt="search icon"
                  src={isHomePage ? searchIconOrange : searchIconBlue}
                  layout="fixed"
                  height={18}
                  width={18}
                  loading="eager"
                />
              </div>
            </button>
          }
          rightPlaceComponent={
            searchValue ? (
              <button
                className="h-full flex items-center pr-3.5"
                type="button"
                onClick={() => {
                  handleCleanInput();
                  trackGtagEvent(
                    `${gtagPrefix}_clean`,
                    searchValue,
                    'search_request'
                  );
                }}
              >
                <div>
                  <NextImage
                    alt="clear icon"
                    src={clearIcon}
                    layout="fixed"
                    height={18}
                    width={18}
                  />
                </div>
              </button>
            ) : null
          }
        />
        <Modal
          style={style}
          id="search-input-v2-modal"
          position={modalPosition()}
          roundingMode={ModalRounding.Bottom}
          triggerElementRef={parentRef || inputRef}
          onClose={() => {
            hideSearchResults();
            trackGtagEvent(`${gtagPrefix}_close`);
          }}
          childrenContainerClass="w-screen xl:w-[913px]"
          withShadowBackground={withShadowBackground}
          modalContainerClassName={cn(
            modalContainerClassName,
            isOnHeader && 'mt-[112px] sm:mt-0'
          )}
          globalContainerClassName={globalContainerClassName}
          fixedModal={false}
        >
          <SearchResults
            categoriesList={initialCategoriesList || null}
            drugList={drugListWithAnalyticData}
            tradeNamesList={tradeNamesData}
            showRecommendations={showRecommendations}
            searchValue={searchValue}
            searchInProgress={searchInProgress || isLoading}
            isSearchTried={isSearchTried}
            gtagPrefix={gtagPrefix}
            hideSearchResults={hideSearchResults}
            onCategoryHover={onCategoryHover}
            onCategoryClick={handleSubmit}
            onViewAllCategoryClick={handleSubmit}
            selectedCategoryId={selectedCategoryId}
            onHistoryElementClick={handleRedirectToSearchWithValue}
            showMore={handleSubmit}
            clearField={() => debouncedOnSearchValueUpdate('')}
          />
        </Modal>
      </form>
    </div>
  );
});

SearchInputV2.displayName = 'SearchInputV2';

export default SearchInputV2;
